import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import QRCode from 'react-qr-code';

import { wallsApi } from '@walls/services/wallsApi';
import type { WallBoard } from '@walls/store';

interface ShareDialogProps {
  board: WallBoard;
  onClose: () => void;
  open: boolean;
  topicKey?: string | null;
}

type CopyStatus = 'link' | 'embed' | 'error' | null;

export function ShareDialog({ board, onClose, open, topicKey }: ShareDialogProps) {
  const [copyStatus, setCopyStatus] = useState<CopyStatus>(null);
  const [emailAddresses, setEmailAddresses] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [sendState, setSendState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [emailVerified, setEmailVerified] = useState<boolean | null>(null);
  const [verificationNotice, setVerificationNotice] = useState('');

  useEffect(() => {
    if (!open) return;
    setEmailVerified(null);
    let cancelled = false;
    void wallsApi.getSharingVerification()
      .then(({ user }) => { if (!cancelled) setEmailVerified(user.emailVerified !== false); })
      .catch(() => { if (!cancelled) setVerificationNotice('Unable to check email verification. Try opening Share again.'); });
    return () => { cancelled = true; };
  }, [open]);

  const shareUrl = useMemo(() => getBoardShareUrl(board.id), [board.id]);
  const embedCode = useMemo(
    () =>
      `<iframe width="850" height="615" src="${shareUrl}" frameborder="0" allowfullscreen></iframe>`,
    [shareUrl]
  );

  const copyToClipboard = async (value: string, status: Exclude<CopyStatus, 'error' | null>) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopyStatus(status);
    } catch {
      setCopyStatus('error');
    }
  };

  const sendInvitations = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!emailAddresses.trim()) {
      setEmailError(true);
      return;
    }

    setEmailError(false);
    setSendState('sending');

    try {
      await wallsApi.sendInvitations(board.id, emailAddresses, emailMessage, topicKey);
      setEmailAddresses('');
      setEmailMessage('');
      setSendState('sent');
    } catch (error) {
      console.error('Invitation send failed', error);
      setSendState('error');
    }
  };

  if (emailVerified === null) {
    return <Dialog open={open} onClose={onClose}><DialogContent>{verificationNotice || 'Checking email verification...'}</DialogContent></Dialog>;
  }

  if (emailVerified === false) {
    return <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Verify your email to share</DialogTitle>
      <DialogContent><Stack spacing={2}>
        <Alert severity="warning">Only verified email adresses may share</Alert>
        <Typography>Check your inbox for a verification link sent to your email address.</Typography>
        {verificationNotice && <Typography>{verificationNotice}</Typography>}
      </Stack></DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button onClick={() => { void wallsApi.requestEmailVerification()
          .then(() => setVerificationNotice('Verification email sent.'))
          .catch((error) => setVerificationNotice(error instanceof Error ? error.message : 'Unable to send verification email.')); }}>Resend email</Button>
      </DialogActions>
    </Dialog>;
  }

  if (!board.isPublic) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Invite via e-mail</DialogTitle>
        {sendState === 'sent' ? (
          <>
            <DialogContent>
              <Typography>Your invitations have been sent.</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} variant="contained">
                Done
              </Button>
            </DialogActions>
          </>
        ) : (
          <Box component="form" onSubmit={sendInvitations}>
            <DialogContent>
              <Stack spacing={2}>
                <Typography variant="body2">
                  Enter a comma separated list of e-mail addresses to be invited
                </Typography>
                <TextField
                  autoFocus
                  error={emailError}
                  fullWidth
                  helperText={emailError ? 'Required' : undefined}
                  label="E-mail addresses"
                  onChange={(event) => {
                    setEmailAddresses(event.target.value);
                    setEmailError(false);
                  }}
                  value={emailAddresses}
                />
                <TextField
                  fullWidth
                  label="Message"
                  minRows={4}
                  multiline
                  onChange={(event) => setEmailMessage(event.target.value)}
                  value={emailMessage}
                />
                {sendState === 'error' && (
                  <Alert severity="error">Invitations could not be sent. Please try again.</Alert>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose}>Cancel</Button>
              <Button disabled={sendState === 'sending'} type="submit" variant="contained">
                Send
              </Button>
            </DialogActions>
          </Box>
        )}
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Invite via Link</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {copyStatus === 'link' && <Alert severity="success">Link copied</Alert>}
          {copyStatus === 'embed' && <Alert severity="success">Code copied</Alert>}
          {copyStatus === 'error' && (
            <Alert severity="error">Sorry, your browser does not support automatic copying</Alert>
          )}
          <Typography variant="body2">
            Participants can be invited to join by sending them the following link:
          </Typography>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField
              aria-label="Invite link"
              fullWidth
              inputProps={{ readOnly: true }}
              size="small"
              value={shareUrl}
            />
            <Tooltip title="Copy to clipboard">
              <IconButton
                aria-label="Copy invite link"
                onClick={() => copyToClipboard(shareUrl, 'link')}
              >
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Stack>
          <Typography variant="body2">or it can be embedded using the following code:</Typography>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField
              aria-label="Embed code"
              fullWidth
              inputProps={{ readOnly: true }}
              multiline
              size="small"
              value={embedCode}
            />
            <Tooltip title="Copy to clipboard">
              <IconButton
                aria-label="Copy embed code"
                onClick={() => copyToClipboard(embedCode, 'embed')}
              >
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Stack>
          <Box sx={{ alignSelf: 'center', bgcolor: 'common.white', p: 2 }}>
            <QRCode size={220} value={shareUrl} />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function getBoardShareUrl(boardId: string) {
  const { origin } = window.location;

  return `${origin}/w/${boardId}`;
}
